import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { api, getToken, setToken, extractToken } from "@/lib/api";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [termsVersion, setTermsVersion] = useState(null);

  const loadTerms = useCallback(async () => {
    try {
      const { data } = await api.get("/terms/current");
      setTermsVersion(data?.version || null);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const fetchMe = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const checkAuth = useCallback(async () => {
    // Returning from OAuth (session_id in query OR hash): let AuthCallback exchange first.
    if (window.location.hash?.includes("session_id=") || window.location.search?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Bearer-token auth: with no stored token the user is logged out — show
    // the app instantly instead of hanging on a cross-domain /auth/me call.
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      await fetchMe();
      await loadTerms();
    } catch (e) {
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [fetchMe, loadTerms]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Exchange one-time session_id for a session token, then load user.
  const exchangeSession = useCallback(
    async (sessionId) => {
      const { data } = await api.post("/auth/session", { session_id: sessionId });
      const token = extractToken(data);
      if (token) setToken(token);
      const me = await fetchMe();
      await loadTerms();
      return me;
    },
    [fetchMe, loadTerms]
  );

  const refreshUser = useCallback(async () => {
    try {
      await fetchMe();
    } catch (e) {
      /* ignore */
    }
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    setToken(null);
    setUser(null);
  }, []);

  const acceptedTerms = !!user && termsVersion && user.accepted_terms_version === termsVersion;

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, termsVersion, acceptedTerms, exchangeSession, refreshUser, logout, checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};
